import './WidgetStyle.css'; 
import './ResponsiveFonts.css';
import { useFont } from '../../Design/FontContext';
import React, { useState, useEffect, useRef } from 'react';
import DeleteModal from './DeleteModal';
import { getTextEffectStyles } from '../utils/textEffectsUtils';
import '../WidgetEditComponents/TextEffectsAnimations.css';
import { generateLayoutStyles, getResponsiveValue, appendPx } from '../WidgetEditComponents/LayoutUtils';
import AIRefreshIcon from '../../components/AIRefreshIcon';

function SpiritualQuoteCard({ data, onRemove, isSelected, isEditMode, onUpdate, onSelectWidget }) {
    const deleteLogo = '/img/delete.svg';
    const fonts = useFont();
    
    // SIMPLE STATES (no inline editing needed)
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    
    // Animation Trigger State (2025 Animation System)
    const [animationKey, setAnimationKey] = useState(0);
    
    const widgetRef = useRef(null);

    // Animation Trigger Effect (2025 Animation System)
    useEffect(() => {
        if (data.triggerAnimation && isEditMode) {
            setAnimationKey(prev => prev + 1);
        }
    }, [data.triggerAnimation, isEditMode]);

    // Force re-render when needed (containerStyle ist jetzt fest auf modern-flat)
    useEffect(() => {
        if (isEditMode) {
            setAnimationKey(prev => prev + 1);
        }
    }, [isEditMode]);

    // 🚀 REVOLUTIONARY LAYOUT SYSTEM - Mobile Breakpoint Detection (750px)
    const [isMobileLayout, setIsMobileLayout] = useState(false);
    const containerRef = useRef(null);
    
    useEffect(() => {
        const checkContainerWidth = () => {
            if (containerRef.current) {
                const containerWidth = containerRef.current.offsetWidth;
                setIsMobileLayout(containerWidth < 750); // ✅ Standardized Breakpoint
            }
        };

        checkContainerWidth();
        window.addEventListener('resize', checkContainerWidth);
        
        const observer = new ResizeObserver(checkContainerWidth);
        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => {
            window.removeEventListener('resize', checkContainerWidth);
            observer.disconnect();
        };
    }, []);

    // Backwards compatibility for responsive size logic
    const isMobile = isMobileLayout;
    const isBig = !isMobileLayout && window.innerWidth > 1200;

    // FONT LOGIC
    const getFont = (fontField, fallback = 'Arial, sans-serif') => {
        if (data[fontField] && data[fontField].trim() !== '') {
            return data[fontField];
        }
        
        if (fontField.includes('title')) {
            return fonts.globalBigFont || fallback;
        } else {
            return fonts.globalSmallFont || fallback;
        }
    };

    // RESPONSIVE SIZE LOGIC
    const getResponsiveSize = (baseField) => {
        if (isBig && data[`${baseField}Big`]) {
            return data[`${baseField}Big`];
        } else if (isMobile && data[`${baseField}Mobile`]) {
            return data[`${baseField}Mobile`];
        }
        return data[baseField] || 16;
    };

    // STYLE CALCULATIONS
    function isDark(color) {
        const rgb = parseInt(color.slice(1), 16);
        const r = (rgb >> 16) & 0xff;
        const g = (rgb >>  8) & 0xff;
        const b = (rgb >>  0) & 0xff;
        const luma = 0.299 * r + 0.587 * g + 0.114 * b;
        return luma < 128;
    }

    function getBorderStyle() {
        if (isEditMode) {
            if (isSelected) {
                const borderColor = isDark(data.backgroundColor) ? '#FFFFFF' : '#000000';
                return `2px dashed ${borderColor}`;
            } else {
                return data.border ? `1.5px solid ${data.borderColor}` : '2px dashed transparent';
            }
        } else {
            return data.border ? `1.5px solid ${data.borderColor}` : 'none';
        }
    }

    // DELETE HANDLERS
    const handleDelete = (event) => {
        onRemove(event, data.id);
        setShowDeleteModal(false);
    };

    // WIDGET CLICK HANDLER
    const handleWidgetClick = (event) => {
        if (isEditMode && onSelectWidget && event.target === event.currentTarget) {
            onSelectWidget(data.id);
        }
    };

    // 🚀 NEW LAYOUT SYSTEM 2025 - Responsive Widget Styles
    const layoutStyles = generateLayoutStyles(data, isMobileLayout, {
        backgroundColor: data.backgroundColor,
        border: getBorderStyle(),
        borderRadius: appendPx(data.borderRadius),
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        maxWidth: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: isEditMode ? 'pointer' : 'default'
    });

    const widgetStyle = {
        ...layoutStyles,
        // ✅ FALLBACK für alte Height-Property (Backwards Compatibility)
        ...(layoutStyles.height === 'auto' && !layoutStyles.minHeight && {
            minHeight: appendPx(isMobileLayout ? 
                (data.heightMobile || 1200) : // Mobile Default
                (data.height || 400)         // Desktop Default
            )
        })
    };

    // BACKGROUND IMAGE STYLES
    const backgroundImageStyle = data.backgroundImage ? {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundImage: `url(${data.backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        opacity: data.backgroundImageOpacity || 0.7,
        filter: data.imageEffect === 'blur' ? 'blur(3px)' : 
                data.imageEffect === 'grayscale' ? 'grayscale(100%)' : 'none',
        zIndex: 1
    } : {};

    // LAYOUT CONFIGURATION - Multiple Layouts mit Responsive System
    const getLayoutConfig = () => {
        const layoutType = data.layoutType || 'modern-flat';
        
        // Responsive Werte mit getResponsiveValue
        const containerPadding = getResponsiveValue(data, 'containerPadding', isMobileLayout, 48);
        const containerGap = getResponsiveValue(data, 'iconTitleSpacing', isMobileLayout, 20);
        const textAlignment = getResponsiveValue(data, 'textAlignment', isMobileLayout, 'center');
        const textMarginX = getResponsiveValue(data, 'textMarginX', isMobileLayout, 0);
        
        const layouts = {
            'modern-flat': {
                // Layout Properties - Responsive
                containerDirection: 'column',
                containerAlign: textAlignment === 'center' ? 'center' : 
                               textAlignment === 'left' ? 'flex-start' : 'flex-end',
                textAlign: textAlignment,
                containerGap: containerGap,
                containerPadding: containerPadding,
                textMarginX: textMarginX,
                
                // Visual Properties - Full Width, Clean Design
                background: data.containerBackground || data.backgroundColor || 'transparent',
                border: 'none',
                boxShadow: 'none',
                backdropFilter: 'none',
                
                description: 'Full width flat design with straight edges'
            },
            'elegant-minimal': {
                // Layout Properties - Elegante Abstände
                containerDirection: 'column',
                containerAlign: textAlignment === 'center' ? 'center' : 
                               textAlignment === 'left' ? 'flex-start' : 'flex-end',
                textAlign: textAlignment,
                containerGap: containerGap,
                containerPadding: Math.max(containerPadding, isMobileLayout ? 40 : 64), // Mehr Padding für Eleganz
                textMarginX: textMarginX,
                
                // Visual Properties - Subtile Eleganz
                background: data.containerBackground || data.backgroundColor || 'rgba(255, 255, 255, 0.95)',
                border: '1px solid rgba(0, 0, 0, 0.08)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.06), 0 2px 8px rgba(0, 0, 0, 0.04)',
                backdropFilter: 'blur(8px) saturate(120%)',
                
                description: 'Elegant minimal design with subtle shadows and refined spacing'
            },
            'luxury-minimal': {
                // Layout Properties - Premium Spacing
                containerDirection: 'column',
                containerAlign: textAlignment === 'center' ? 'center' : 
                               textAlignment === 'left' ? 'flex-start' : 'flex-end',
                textAlign: textAlignment,
                containerGap: Math.max(containerGap, isMobileLayout ? 24 : 32), // Großzügigere Gaps
                containerPadding: Math.max(containerPadding, isMobileLayout ? 48 : 80), // Premium Padding
                textMarginX: textMarginX,
                
                // Visual Properties - Luxuriöse Zurückhaltung
                background: data.containerBackground || data.backgroundColor || 'rgba(248, 250, 252, 0.98)',
                border: '1px solid rgba(148, 163, 184, 0.15)',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.08), 0 8px 24px rgba(0, 0, 0, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.6)',
                backdropFilter: 'blur(12px) saturate(130%)',
                
                description: 'Luxury minimal design with premium spacing and sophisticated effects'
            }
        };
        
        return layouts[layoutType] || layouts['modern-flat'];
    };

    // CONTAINER STYLES - Layout-abhängig
    const layoutConfig = getLayoutConfig();
    const layoutType = data.layoutType || 'modern-flat';
    
    const containerStyle = {
        position: 'relative',
        zIndex: 2,
        // ✅ FULL WIDTH: Keine maxWidth Begrenzung
        width: '100%',
        
        // ✅ VISUAL PROPERTIES aus Layout-Config
        background: layoutConfig.background,
        border: layoutConfig.border,
        boxShadow: layoutConfig.boxShadow,
        backdropFilter: layoutConfig.backdropFilter,
        
        // ✅ LAYOUT PROPERTIES - Layout-abhängige Rundungen
        borderRadius: layoutType === 'modern-flat' ? '0px' : 
                     layoutType === 'elegant-minimal' ? '16px' :
                     layoutType === 'luxury-minimal' ? '20px' : '0px',
        padding: appendPx(layoutConfig.containerPadding),
        textAlign: layoutConfig.textAlign,
        display: 'flex',
        flexDirection: layoutConfig.containerDirection,
        alignItems: layoutConfig.containerAlign,
        gap: appendPx(layoutConfig.containerGap),
        
        // ✅ ZUSÄTZLICHE ELEGANZ für minimale Layouts
        ...(layoutType !== 'modern-flat' && {
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: 'translateZ(0)', // Hardware-Beschleunigung für smooth effects
        })
    };

    // TEXT CONTENT STYLES - Mit responsive Text-Margins
    const textContentStyle = {
        width: '100%',
        marginLeft: appendPx(layoutConfig.textMarginX || 0),
        marginRight: appendPx(layoutConfig.textMarginX || 0),
        display: 'flex',
        flexDirection: 'column',
        alignItems: layoutConfig.containerAlign,
        gap: appendPx(layoutConfig.containerGap)
    };

    // TOP ICON STYLES - Mit Invert Toggle statt automatischer Invertierung
    const topIconStyle = data.showTopIcon !== false ? {
        width: appendPx(data.topIconSize || 48),
        height: appendPx(data.topIconSize || 48),
        // ✅ NEUES SYSTEM: Nur invertieren wenn topIconInvert aktiviert ist
        filter: data.topIconInvert ? 
            `brightness(0) saturate(100%) invert(100%)` : // Invertiert
            'none', // Normal (für Bilder und farbige Icons)
        marginBottom: appendPx(data.iconTitleSpacing || 20)
    } : { display: 'none' };

    // TITLE STYLES (mit responsive Text-Alignment)
    const titleStyles = {
        color: data.titleColor,
        fontSize: appendPx(getResponsiveSize('titleSize')),
        fontWeight: data.titleWeight,
        fontFamily: getFont('titleFont'),
        margin: `0 0 ${appendPx(getResponsiveValue(data, 'titleSymbolSpacing', isMobileLayout, 16))} 0`,
        lineHeight: 1.2,
        textAlign: layoutConfig.textAlign, // ✅ Responsive Text-Alignment
        ...getTextEffectStyles(data.titleEffect)
    };

    // DECORATIVE SYMBOL STYLES (mit responsive Text-Alignment)
    const symbolStyle = {
        color: data.decorativeSymbolColor,
        fontSize: appendPx(getResponsiveValue(data, 'decorativeSymbolSize', isMobileLayout, 16)),
        margin: `0 0 ${appendPx(getResponsiveValue(data, 'symbolTextSpacing', isMobileLayout, 24))} 0`,
        textAlign: layoutConfig.textAlign, // ✅ Responsive Text-Alignment
        opacity: 0.8
    };

    // RICH TEXT STYLES (mit responsive Text-Alignment)
    const richTextStyle = {
        color: data.richTextColor,
        fontSize: appendPx(getResponsiveSize('richTextSize')),
        fontWeight: data.richTextWeight,
        fontFamily: getFont('richTextFont'),
        lineHeight: isMobile ? (data.richTextLineHeightMobile || 1.5) : (data.richTextLineHeight || 1.6),
        margin: 0,
        textAlign: layoutConfig.textAlign, // ✅ Responsive Text-Alignment
        ...getTextEffectStyles(data.richTextEffect)
    };

    return (
        <div 
            ref={(el) => { containerRef.current = el; widgetRef.current = el; }}
            className="spiritual-quote-card-widget layout-modern-flat"
            style={widgetStyle} 
            onClick={handleWidgetClick}
            key={animationKey}
        >
            {/* Background Image */}
            {data.backgroundImage && (
                <div style={backgroundImageStyle}></div>
            )}

            {/* Delete Button */}
            {isEditMode && (
                <button 
                    className="delete_button" 
                    onClick={() => setShowDeleteModal(true)}
                >
                    <img className="delete_logo" src={deleteLogo} alt="Löschen" />
                </button>
            )}
            
            {/* 🤖 AI REFRESH ICON */}
            {isEditMode && (
                <AIRefreshIcon 
                    widget={{
                        ...data,
                        type: 'SpiritualQuoteCard'
                    }}
                    spaceId={null}
                    onContentRefreshed={(refreshedWidget) => {
                        console.log('🤖 [SPIRITUALQUOTECARD] AI-refreshed content received:', refreshedWidget);
                        onUpdate(refreshedWidget);
                    }}
                    position="top-left"
                />
            )}

            {/* Delete Modal */}
            {isEditMode && showDeleteModal && (
                <DeleteModal
                    isOpen={showDeleteModal}
                    onClose={() => setShowDeleteModal(false)}
                    onConfirm={handleDelete}
                />
            )}

            {/* Main Content Container */}
            <div style={containerStyle}>
                {/* Top Icon */}
                {data.showTopIcon !== false && data.topIcon && (
                    <img
                        src={data.topIcon}
                        alt="Icon"
                        style={topIconStyle}
                    />
                )}

                {/* Text Content Container - Mit responsive Text-Margins */}
                <div style={textContentStyle}>
                    {/* Title - Mit Show/Hide Toggle */}
                    {data.showTitleText !== false && (
                        <h2 style={titleStyles}>
                            {data.titleText}
                        </h2>
                    )}

                    {/* Decorative Symbol */}
                    {data.showDecorativeSymbol !== false && (
                        <div style={symbolStyle}>
                            {data.decorativeSymbol}
                        </div>
                    )}

                    {/* Rich Text Content - Mit Show/Hide Toggle */}
                    {data.showRichText !== false && (
                        <div 
                            style={richTextStyle}
                            dangerouslySetInnerHTML={{ __html: data.richText }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

export default SpiritualQuoteCard;